import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Download, ShieldCheck } from 'lucide-react';
import { axiosInstance } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MarcoFichaje } from '@/components/MarcoFichaje';
import { DialogoCorreccion } from '@/components/fichajes/DialogoCorreccion';
import { SolicitudesPendientes } from '@/components/fichajes/SolicitudesPendientes';
import { ExcesosDelMes } from '@/components/fichajes/ExcesosDelMes';
import { RegistroAccesos } from '@/components/fichajes/RegistroAccesos';
import {
  fechaCorta, horaLocal, horasMinutos, hoyEn, semanaDe, sumarDias, textoAviso,
} from '@/lib/fichajes';

/**
 * Equipo: el registro de jornada de toda la plantilla.
 *
 * El admin corrige desde aquí y resuelve lo que pide la plantilla; el
 * representante de los trabajadores lo ve en solo lectura. Corregir NUNCA
 * borra: añade fichajes y anula los que sobran, con un motivo obligatorio, y
 * al trabajador le llega un email con el cambio.
 */

const ZONA_INICIAL = 'Europe/Madrid';

function textoCelda(j, zona) {
  const entrada = horaLocal(j.entrada, zona);
  if (j.salida) return `${entrada}–${horaLocal(j.salida, zona)}`;
  if (j.estado === 'abierta') return `${entrada}– en curso`;
  return `${entrada}– sin salida`;
}

function primeroYUltimoDelMes(fecha) {
  const [a, m] = fecha.split('-').map(Number);
  const ultimo = new Date(Date.UTC(a, m, 0)).getUTCDate();
  const mm = String(m).padStart(2, '0');
  return { desde: `${a}-${mm}-01`, hasta: `${a}-${mm}-${String(ultimo).padStart(2, '0')}` };
}

async function descargar(url, params, nombre, tipo) {
  const r = await axiosInstance.get(url, { params, responseType: 'blob' });
  const enlace = window.URL.createObjectURL(new Blob([r.data], { type: tipo }));
  const a = document.createElement('a');
  a.href = enlace;
  a.download = nombre;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(enlace);
}

// ----------------------------------------------------------- configuración ---

function Configuracion() {
  const [cfg, setCfg] = useState(null);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    axiosInstance.get('/fichajes/config').then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  if (!cfg) return null;

  const guardar = async () => {
    setGuardando(true);
    try {
      const r = await axiosInstance.put('/fichajes/config', {
        ...cfg, umbral_olvido_horas: Number(cfg.umbral_olvido_horas),
      });
      setCfg(r.data);
      toast.success('Configuración guardada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido guardar');
    } finally {
      setGuardando(false);
    }
  };

  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Configuración del registro</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label htmlFor="cfg-zona">Zona horaria</Label>
            <Input id="cfg-zona" value={cfg.zona_horaria} onChange={(e) => setCfg({ ...cfg, zona_horaria: e.target.value })} />
            <p className="text-xs text-slate-500 mt-1">Canarias: Atlantic/Canary</p>
          </div>
          <div>
            <Label htmlFor="cfg-umbral">Salida olvidada a las (horas)</Label>
            <Input id="cfg-umbral" type="number" min="4" max="48" value={cfg.umbral_olvido_horas}
              onChange={(e) => setCfg({ ...cfg, umbral_olvido_horas: e.target.value })} />
          </div>
          <div>
            <Label htmlFor="cfg-centro">Centro de trabajo</Label>
            <Input id="cfg-centro" value={cfg.centro_trabajo}
              onChange={(e) => setCfg({ ...cfg, centro_trabajo: e.target.value })}
              placeholder="Si se deja vacío, la dirección de la empresa" />
          </div>
        </div>
        <div>
          <Label htmlFor="cfg-desplaz">Texto sobre desplazamientos (sale en la pantalla Fichar)</Label>
          <Textarea id="cfg-desplaz" value={cfg.texto_desplazamientos}
            onChange={(e) => setCfg({ ...cfg, texto_desplazamientos: e.target.value })} />
        </div>
        <Button onClick={guardar} disabled={guardando} variant="outline">Guardar configuración</Button>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ página ---

export default function EquipoFichajes({ user }) {
  const [semana, setSemana] = useState(() => semanaDe(hoyEn(ZONA_INICIAL)));
  const [datos, setDatos] = useState(null);
  const [edicion, setEdicion] = useState(null);
  const [exportar, setExportar] = useState(() => primeroYUltimoDelMes(hoyEn(ZONA_INICIAL)));
  // Sube cada vez que cambia el registro (corrección o solicitud aprobada).
  const [refresco, setRefresco] = useState(0);

  const cargar = useCallback(async () => {
    try {
      const r = await axiosInstance.get('/fichajes/equipo', { params: { desde: semana.desde, hasta: semana.hasta } });
      setDatos(r.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido cargar el equipo');
    }
  }, [semana]);

  useEffect(() => { cargar(); }, [cargar]);

  const registroCambiado = () => {
    cargar();
    setRefresco((n) => n + 1);
  };

  const zona = datos?.config?.zona_horaria || ZONA_INICIAL;
  const puedeCorregir = !!datos?.puede_corregir;

  // Todo lo que conviene mirar esta semana: jornadas sin salida y los avisos
  // legales (9 h, 12 h de descanso, 6 h sin pausa). No bloquean nada.
  const avisos = (datos?.personas || []).flatMap((p) => p.jornadas
    .filter((j) => j.avisos?.length)
    .map((j) => ({ persona: p, jornada: j })));

  const abrir = (persona, fecha) => {
    if (!puedeCorregir) return;
    setEdicion({ persona, fecha, jornadas: persona.jornadas.filter((j) => j.fecha_local === fecha) });
  };

  const exportarComo = async (formato) => {
    try {
      await descargar('/fichajes/export', { ...exportar, formato },
        `registro-jornada-${exportar.desde}-a-${exportar.hasta}.${formato}`,
        formato === 'pdf' ? 'application/pdf' : 'text/csv');
    } catch (error) {
      toast.error('No se ha podido exportar');
    }
  };

  const verificar = async () => {
    try {
      const r = await axiosInstance.get('/fichajes/verificar');
      if (r.data.problemas.length === 0) {
        toast.success(`Registro íntegro: ${r.data.personas_revisadas} persona(s) revisadas, nada alterado.`);
      } else {
        toast.error(`La cadena no cuadra en ${r.data.problemas.length} persona(s). Revisa antes de exportar.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No se ha podido comprobar');
    }
  };

  return (
    <MarcoFichaje user={user} ancho>
      <div className="space-y-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Registro de jornada · Equipo</h1>
            <p className="text-slate-600 text-sm">
              {puedeCorregir ? 'Pulsa en un día para corregirlo.' : 'Consulta en solo lectura.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={() => setSemana(semanaDe(sumarDias(semana.desde, -7)))}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm font-medium tabular-nums">
              {fechaCorta(semana.desde)} – {fechaCorta(semana.hasta)}
            </span>
            <Button variant="outline" size="icon" onClick={() => setSemana(semanaDe(sumarDias(semana.desde, 7)))}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <SolicitudesPendientes puedeResolver={puedeCorregir} alResolver={registroCambiado} />

        {avisos.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-1">
            <p className="text-sm font-semibold text-amber-900">
              {avisos.length} jornada{avisos.length > 1 ? 's' : ''} que conviene revisar
            </p>
            {avisos.map(({ persona, jornada }) => (
              <div key={jornada.jornada_id} className="flex items-center justify-between gap-2 text-sm text-amber-900">
                <span>
                  {persona.name} · {fechaCorta(jornada.fecha_local)} · entrada {horaLocal(jornada.entrada, zona)}
                  {' · '}{jornada.avisos.map(textoAviso).join(', ')}
                </span>
                {puedeCorregir && jornada.avisos.includes('sin_salida') && (
                  <Button size="sm" variant="outline" onClick={() => abrir(persona, jornada.fecha_local)}>Corregir</Button>
                )}
              </div>
            ))}
          </div>
        )}

        <Card>
          <CardContent className="pt-4 overflow-x-auto">
            {!datos ? (
              <p className="text-slate-500">Cargando…</p>
            ) : datos.personas.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                Nadie registra jornada todavía. Márcalo en Usuarios (rol «Trabajador» o «Registra jornada»).
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-3 font-medium">Persona</th>
                    {semana.dias.map((d) => <th key={d} className="py-2 px-1 font-medium">{fechaCorta(d)}</th>)}
                    <th className="py-2 pl-2 font-medium text-right">Semana</th>
                  </tr>
                </thead>
                <tbody>
                  {datos.personas.map((p) => {
                    const total = p.jornadas.reduce((s, j) => s + (j.estado === 'cerrada' ? j.minutos_trabajados : 0), 0);
                    return (
                      <tr key={p.id} className="border-b last:border-0 align-top">
                        <td className="py-2 pr-3">
                          <p className="font-medium">{p.name}</p>
                          {!p.is_active && <p className="text-xs text-slate-500">desactivado</p>}
                        </td>
                        {semana.dias.map((d) => {
                          const delDia = p.jornadas.filter((j) => j.fecha_local === d);
                          return (
                            <td key={d} className="py-1 px-1">
                              <button
                                type="button"
                                onClick={() => abrir(p, d)}
                                disabled={!puedeCorregir}
                                className={`w-full min-h-[2.5rem] text-left rounded px-1.5 py-1 tabular-nums text-xs ${
                                  puedeCorregir ? 'hover:bg-slate-100' : 'cursor-default'
                                }`}
                              >
                                {delDia.map((j) => (
                                  <span key={j.jornada_id} className={`block ${j.avisos?.length ? 'text-amber-700 font-semibold' : ''}`}>
                                    {textoCelda(j, zona)}{j.corregida ? ' *' : ''}
                                    {j.minutos_trabajados != null && (
                                      <span className="block text-slate-500 font-normal">{horasMinutos(j.minutos_trabajados)} h</span>
                                    )}
                                    {j.avisos?.filter((a) => a !== 'sin_salida').map((a) => (
                                      <span key={a} className="block font-normal">{textoAviso(a)}</span>
                                    ))}
                                  </span>
                                ))}
                              </button>
                            </td>
                          );
                        })}
                        <td className="py-2 pl-2 text-right font-semibold tabular-nums">{horasMinutos(total)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <p className="text-xs text-slate-500 mt-3">* Corregida: lo anterior sigue en el registro, con su motivo.</p>
          </CardContent>
        </Card>

        <ExcesosDelMes mesInicial={hoyEn(ZONA_INICIAL).slice(0, 7)} refresco={refresco} />

        <Card>
          <CardHeader><CardTitle className="text-base">Para la Inspección de Trabajo</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div>
              <Label htmlFor="exp-desde">Desde</Label>
              <Input id="exp-desde" type="date" value={exportar.desde}
                onChange={(e) => setExportar({ ...exportar, desde: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="exp-hasta">Hasta</Label>
              <Input id="exp-hasta" type="date" value={exportar.hasta}
                onChange={(e) => setExportar({ ...exportar, hasta: e.target.value })} />
            </div>
            <Button onClick={() => exportarComo('pdf')}><Download className="w-4 h-4 mr-2" />Exportar PDF</Button>
            <Button variant="outline" onClick={() => exportarComo('csv')}><Download className="w-4 h-4 mr-2" />CSV</Button>
            {puedeCorregir && (
              <Button variant="outline" onClick={verificar}><ShieldCheck className="w-4 h-4 mr-2" />Comprobar integridad</Button>
            )}
          </CardContent>
        </Card>

        {puedeCorregir && <RegistroAccesos zona={zona} />}
        {puedeCorregir && <Configuracion />}
      </div>

      <DialogoCorreccion
        modo="corregir"
        abierto={!!edicion}
        alCerrar={() => setEdicion(null)}
        persona={edicion?.persona}
        fecha={edicion?.fecha}
        jornadas={edicion?.jornadas || []}
        zona={zona}
        alGuardar={() => { setEdicion(null); registroCambiado(); }}
      />
    </MarcoFichaje>
  );
}
